// link.service.ts (Angular 2.3.1 friendly)
import { Injectable } from '@angular/core';
import 'rxjs/add/operator/map';
import { CONSTANT_VALUES } from '../../constants/constant-values';
import { HttpInterceptor } from '../../shared/index';
import { AppService } from '../../app.service';
import { LinkFacility } from './link-facility.interface';
import { UtilityFunctions } from '../../shared/utility-functions';
import { ApiResponse } from './api-response.interface';


@Injectable()
export class LinkService {
  private _appService;
  private utilityFunctions = new UtilityFunctions();

  constructor(private http: HttpInterceptor, appService: AppService) {
    this._appService = appService;
  }

  // Helper: safe parse response (Angular 2 Http returns Response)
  private parseJsonResponse(res: any): any {
    try {
      return res.json ? res.json() : res;
    } catch (e) {
      return res;
    }
  }

  addLink(linkObject: LinkFacility): Promise<ApiResponse> {
    const url = CONSTANT_VALUES.URL_CREATE_OR_UPDATE_FACILITY;
    return new Promise<ApiResponse>((resolve, reject) => {
      this.http.post(url, linkObject)
        .map(res => this.parseJsonResponse(res))
        .subscribe(
          (data: ApiResponse) => resolve(data),
          error => reject(error)
        );
    });
  }

  updateLink(link: LinkFacility): Promise<ApiResponse> {
    const url = CONSTANT_VALUES.URL_CREATE_OR_UPDATE_FACILITY;
    return new Promise<ApiResponse>((resolve, reject) => {
      this.http.put(url, link)
        .map(res => this.parseJsonResponse(res))
        .subscribe(
          (data: ApiResponse) => resolve(data),
          error => reject(error)
        );
    });
  }

  deleteLink(link: { facilityId: string; vendorId: string }): Promise<ApiResponse> {
    const url = CONSTANT_VALUES.URL_DELETE_FACILITY;
    return new Promise<ApiResponse>((resolve, reject) => {
      this.http.post(url, link)
        .map(res => this.parseJsonResponse(res))
        .subscribe(
          (data: ApiResponse) => resolve(data),
          error => reject(error)
        );
    });
  }

  // date helpers
  private todayZero(): Date {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }

  private maxFutureDateYears(years = 2): Date {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  validateLinkDate(linkDate: string) {
    const response: any = { status: 300, message: '' };
    if (!linkDate || ('' + linkDate).trim() === '') {
      response.status = 400; response.message = 'Link date is required'; return response;
    }
    const selected = new Date(linkDate);
    if (isNaN(selected.getTime())) { response.status = 400; response.message = 'Invalid link date'; return response; }
    selected.setHours(0, 0, 0, 0);
    if (selected < this.todayZero()) { response.status = 400; response.message = 'Link date cannot be in the past'; return response; }
    if (selected > this.maxFutureDateYears(2)) { response.status = 400; response.message = 'Link date cannot be more than 2 years in the future'; return response; }
    return response;
  }

  validateExpiryDate(expiryDate: string) {
    const response: any = { status: 300, message: '' };
    if (!expiryDate || ('' + expiryDate).trim() === '') {
      response.status = 400; response.message = 'Link expiry date is required'; return response;
    }
    const selected = new Date(expiryDate);
    if (isNaN(selected.getTime())) { response.status = 400; response.message = 'Invalid expiry date'; return response; }
    selected.setHours(0, 0, 0, 0);
    if (selected <= this.todayZero()) { response.status = 400; response.message = 'Link expiry date must be in the future'; return response; }
    if (selected > this.maxFutureDateYears(2)) { response.status = 400; response.message = 'Expiry date cannot be more than 2 years in the future'; return response; }
    return response;
  }

  validateCTA(ctaData: any) {
    const response: any = { status: 300, message: '' };
    if (ctaData && ctaData.ctaEnabled) {
      if (!ctaData.ctaText || ('' + ctaData.ctaText).trim() === '') { response.status = 400; response.message = 'CTA text is required'; return response; }
      if (!ctaData.ctaLink || ('' + ctaData.ctaLink).trim() === '') { response.status = 400; response.message = 'CTA link is required'; return response; }
      // light URL check: require host or protocol
      const link = ('' + ctaData.ctaLink).trim();
      const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]+)?(\/.*)?$/i;
      if (!urlRegex.test(link)) { response.status = 400; response.message = 'CTA link must be a valid URL'; return response; }
    }
    return response;
  }

  validateLinkPricing(pricingData: any) {
    const response: any = { status: 300, message: '' };
    if (!pricingData || !pricingData.activityCharges) return response;
    const tiers = ['member', 'nonMember', 'guest', 'corpMember', 'corpNonMember'];
    for (let tier of tiers) {
      const p = pricingData.activityCharges[tier];
      if (p && p.enabled) {
        if (!p.amount || ('' + p.amount).trim() === '') { response.status = 400; response.message = `${tier} amount required`; return response; }
        const amount = parseFloat(p.amount);
        if (isNaN(amount) || amount < 0) { response.status = 400; response.message = `${tier} amount invalid`; return response; }
      }
    }
    return response;
  }

  // validate type example: keep simple
  validateLinkType(linkType: string) {
    const response: any = { status: 300, message: '' };
    if (!linkType || ('' + linkType).trim() === '') { response.status = 400; response.message = 'Link type is required'; return response; }
    return response;
  }

  prepareLinkForSave(link: LinkFacility): LinkFacility {
    link.name = (link.name || '').toString().trim();
    link.notes = (link.notes || '').toString().trim();
    link.linkType = (link.linkType || '').toString().trim();
    link.description = (link.description || '').toString().trim();
    link.ctaText = (link.ctaText || '').toString().trim();
    link.ctaLink = (link.ctaLink || '').toString().trim();
    link.category = 'Links';
    link.isPeakTimeEnabled = false;
    link.isBreakTimeEnabled = false;
    link.isSlotsLocked = true;
    link.slotDuration = '0';
    if (!link.timings) link.timings = { openTime: '00:00', closeTime: '00:00' };
    else { link.timings.openTime = '00:00'; link.timings.closeTime = '00:00'; }
    if (link.maxBookingsPerSlot && ('' + link.maxBookingsPerSlot).toLowerCase() === 'unlimited') link.maxBookingsPerSlot = '999999';
    link.unitNames = link.unitNames || [];
    link.noOfUnits = link.noOfUnits || '';
    link.breakTimings = [];
    link.peekTimings = [];
    link.maxWeekdayBooking = '';
    link.maxWeekendBooking = '';
    link.maxBookingHoursWeekday = '';
    link.maxBookingHoursWeekend = '';
    link.maxBookingHoursMonth = '';
    link.holdBookingDays = '';
    link.peakUnitsCount = '';
    return link;
  }

  // get club links (compatible with both server shapes)
  getClubLinks(vendorObj: any) {
    const url = CONSTANT_VALUES.URL_FACILITY_LIST_BY_VENDOR;
    return new Promise<any>((resolve, reject) => {
      this.http.post(url, vendorObj)
        .map(res => this.parseJsonResponse(res))
        .subscribe((data: any) => {
          if (data && data.status === 'SUCCESS' && data.facilityData) {
            const fd = data.facilityData;
            if (Array.isArray(fd) && fd.length && fd[0].categoryName) {
              const linksData = fd.filter((c: any) => (c.categoryName || '').toString().toLowerCase() === 'links');
              data.linksData = linksData;
              data.facilityData = linksData;
            } else if (Array.isArray(fd)) {
              data.linksData = fd.filter((f: any) => (f.category || '').toString().toLowerCase() === 'links');
              data.facilityData = data.linksData;
            }
          }
          resolve(data);
        }, err => reject(err));
    });
  }

  // current links (filter expired)
  getCurrentLinks(vendorObj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getClubLinks(vendorObj).then((data: any) => {
        if (!data) { resolve({ status: 'ERROR', links: [], message: 'No data returned' }); return; }
        const arr = data.links || data.linksData || data.facilityData || [];
        if (data.status === 'SUCCESS') {
          const today = this.todayZero();
          const currentLinks = (arr || []).filter((l: any) => {
            if (!l || !l.expiryDate) return true;
            const e = new Date(l.expiryDate); if (isNaN(e.getTime())) return true;
            e.setHours(0,0,0,0); return e > today;
          });
          resolve({ status: 'SUCCESS', links: currentLinks });
        } else {
          resolve({ status: 'ERROR', links: [], message: data.message || 'No links' });
        }
      }).catch(err => reject(err));
    });
  }

    /**
   * Backwards-compatible adapter used by older components.
   * Component code expects getLinksForVendor(vendorObj) returning
   * a Promise with { status: 'SUCCESS', links: [...] } or linksData.
   */
  getLinksForVendor(vendorObj: any): Promise<any> {
    // Prefer current links (filters out expired links). If you want
    // raw club data, change to return this.getClubLinks(vendorObj).
    return this.getCurrentLinks(vendorObj)
      .then((res: any) => {
        // ensure older component expectations: provide links and/or linksData
        if (res && res.status === 'SUCCESS') {
          // res.links already exists from getCurrentLinks
          return { status: 'SUCCESS', links: res.links };
        }
        // preserve error shape if present
        return res;
      })
      .catch((err: any) => {
        // keep rejection shape similar to other service calls
        return Promise.reject(err);
      });
  }

   /**
 * Update group(s)
 * - Single: pass { groupId, vendorId, newName?, description?, activities? }
 * - Multiple: pass { groupIds: string[], vendorId, activities? }
 *   (newName/description are ignored by backend for multi per your server logic)
 */
updateGroup(groupObj: {
  vendorId: string;
  groupId?: string;          // single
  groupIds?: string[];       // multiple
  newName?: string;
  description?: string;
  activities?: string[];
}): Promise<any> {
  const url = CONSTANT_VALUES.URL_UPDATE_GROUP;
  return new Promise((resolve, reject) => {
    this.http.post(url, groupObj)
      .map(res => res.json())
      .subscribe(
        data => resolve(data),
        err => reject(err)
      );
  });
}

/**
 * Convenience wrapper when you always have multiple IDs
 */
updateGroups(groupIds: string[], vendorId: string, activities?: string[]): Promise<any> {
  return this.updateGroup({ groupIds, vendorId, activities });
}

  /**
   * Test external API connection for any provider (routes through backend)
   * @param provider - The provider name (shopify, digirestro, custom)
   * @param config - The provider configuration
   */
  public testExternalApiConnection(provider: string, config: any): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        // Validate provider
        if (!provider || typeof provider !== 'string') {
          reject({
            success: false,
            message: 'Provider is required',
            error: new Error('Invalid provider')
          });
          return;
        }

        // Validate config
        if (!config || typeof config !== 'object') {
          reject({
            success: false,
            message: 'Configuration is required',
            error: new Error('Invalid configuration')
          });
          return;
        }

        // Prepare request body
        const requestBody = {
          provider: provider.toLowerCase(),
          config: config
        };

        console.log('Calling backend provider-health-check API...');

        // Call backend API endpoint
        this.http.post('/api/users/provider-health-check', requestBody)
          .map(res => this.parseJsonResponse(res))
          .subscribe(
            (data: any) => {
              console.log('Connection test success:', data);

              // Handle both response formats: {success: true} or {status: 200}
              const isSuccess = (data && data.success) || (data && data.status === 200);

              if (isSuccess) {
                resolve({
                  success: true,
                  message: data.message || 'Connection successful!',
                  data: data.data
                });
              } else {
                reject({
                  success: false,
                  message: data.message || 'Connection test failed',
                  error: data.error
                });
              }
            },
            (error: any) => {
              console.error('Connection test failed:', error);

              let errorMessage = 'Connection test failed';

              try {
                const errorBody = error.json ? error.json() : error;
                if (errorBody && errorBody.message) {
                  errorMessage = errorBody.message;
                } else if (error.statusText) {
                  errorMessage = `HTTP ${error.status}: ${error.statusText}`;
                } else if (error.message) {
                  errorMessage = error.message;
                }
              } catch (e) {
                if (error.statusText) {
                  errorMessage = `HTTP ${error.status}: ${error.statusText}`;
                } else if (error.message) {
                  errorMessage = error.message;
                }
              }

              reject({
                success: false,
                message: errorMessage,
                error: error,
                status: error.status
              });
            }
          );

      } catch (error) {

        reject({
          success: false,
          message: error.message || 'Failed to initiate connection test',
          error: error
        });
      }
    });
  }

}





// WEBPACK FOOTER //
// ./src/app/facility/link/link.service.ts